import { create } from 'zustand';
import * as svc from '../services/mailCatcherService';
import { isTauri } from '../utils/tauri';
import { usePreferencesStore } from './preferencesStore';

/**
 * Mail catcher UI state. NOT persisted — the Rust listener and its SQLite
 * store are the truth (the mockServerStore doctrine): status is re-synced on
 * mount via `refreshStatus()` and the list via `load()`.
 */

export const EMPTY_STATUS = Object.freeze({
  running: false,
  port: null,
  host: '127.0.0.1',
  count: 0,
  unread: 0,
  lastError: null,
});

export const DETAIL_TABS = ['html', 'text', 'headers', 'raw'];

/** The tab a freshly opened message lands on: HTML when it has one, else Text (D9). */
export function defaultTabFor(detail) {
  return detail && typeof detail.html === 'string' ? 'html' : 'text';
}

/**
 * The ONE case fold the search uses — the JS twin of `store::search_fold`
 * in Rust (fix round 1, NB2): Unicode lower-case, minus U+0307, the
 * combining dot `İ` leaves behind, so `istanbul` finds `İstanbul` on both
 * sides and a realtime arrival is listed exactly when a reload would list it.
 */
export function searchFold(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/\u0307/g, '');
}

/** Client-side twin of the store's LIKE search, for realtime arrivals while filtering. */
export function rowMatchesQuery(row, query) {
  const q = searchFold(query).trim();
  if (!q) return true;
  const hay = searchFold(
    [row?.subject, row?.from, ...(row?.to || []), row?.snippet].filter(Boolean).join('\n')
  );
  return hay.includes(q);
}

const errText = (e) => (e && e.message) || String(e);

/** The `start()` in flight, if any — see `start` below. */
let inflightStart = null;
// Bumped by `onAccountSwitched`: a `mail_catcher_list` / `mail_catcher_status`
// issued for the PREVIOUS account db must not repaint over the new one when
// it resolves late (tester finding F1, 08.09.2026 — `select` already had the
// equivalent `selectedId` guard).
let dbGeneration = 0;

export const useMailCatcherStore = create((set, get) => ({
  status: { ...EMPTY_STATUS },
  starting: false,
  messages: [],
  total: 0,
  unread: 0,
  query: '',
  selectedId: null,
  detail: null,
  // True while a newer message is being fetched; the PREVIOUS detail stays on
  // screen meanwhile so the pane never blinks through the empty state.
  detailLoading: false,
  detailTab: 'html',
  raw: null,
  loading: false,
  error: null,

  refreshStatus: async () => {
    const gen = dbGeneration;
    try {
      const status = await svc.status();
      if (gen !== dbGeneration) return; // answered for a db that is no longer current
      set({ status: { ...EMPTY_STATUS, ...status } });
    } catch (e) {
      if (gen !== dbGeneration) return;
      set({ error: errText(e) });
    }
  },

  /**
   * Resolves to the status on success, `null` on failure (error in
   * `status.lastError`). Idempotent while in flight (fix round 1, NB5): a
   * second call during the first — React StrictMode double-invokes the
   * auto-start effect in `tauri dev` — joins the same promise instead of
   * issuing a second `mail_catcher_start`. Rust serialises too; this keeps
   * the invoke count at one.
   */
  start: (port) => {
    if (inflightStart) return inflightStart;
    inflightStart = (async () => {
      set({ starting: true });
      try {
        // Retention lives in the JS prefs; push it before the first message.
        await svc.setKeep(usePreferencesStore.getState().mailCatcherKeep);
        const status = await svc.start(port);
        set({ status: { ...EMPTY_STATUS, ...status }, starting: false });
        return status;
      } catch (e) {
        set((s) => ({
          starting: false,
          status: { ...s.status, running: false, port: null, lastError: errText(e) },
        }));
        return null;
      } finally {
        inflightStart = null;
      }
    })();
    return inflightStart;
  },

  stop: async () => {
    try {
      const status = await svc.stop();
      set({ status: { ...EMPTY_STATUS, ...status } });
    } catch (e) {
      set((s) => ({ status: { ...s.status, lastError: errText(e) } }));
    }
  },

  load: async () => {
    const gen = dbGeneration;
    set({ loading: true });
    try {
      const page = await svc.list({ query: get().query });
      if (gen !== dbGeneration) return; // stale: the account db was swapped meanwhile
      set({
        messages: page.items || [],
        total: page.total ?? 0,
        unread: page.unread ?? 0,
        loading: false,
        error: null,
      });
    } catch (e) {
      if (gen !== dbGeneration) return;
      set({ loading: false, error: errText(e) });
    }
  },

  setQuery: (query) => {
    set({ query });
    return get().load();
  },

  select: async (id) => {
    if (id == null) {
      set({ selectedId: null, detail: null, detailLoading: false, raw: null });
      return;
    }
    // Keep the previous `detail` mounted while the next one loads: nulling it
    // here dropped the pane to "Select a message" and remounted it a few ms
    // later — the "blink" the owner saw when arrowing through the list.
    set({ selectedId: id, detailLoading: true, raw: null });
    try {
      const detail = await svc.get(id);
      if (get().selectedId !== id) return; // user moved on — the newer select owns the flag
      set((s) => {
        const row = s.messages.find((m) => m.id === id);
        const wasUnread = row ? !row.read : false;
        return {
          detail,
          detailLoading: false,
          detailTab: defaultTabFor(detail),
          messages: s.messages.map((m) => (m.id === id ? { ...m, read: true } : m)),
          unread: wasUnread ? Math.max(0, s.unread - 1) : s.unread,
          status: wasUnread ? { ...s.status, unread: Math.max(0, s.status.unread - 1) } : s.status,
        };
      });
    } catch (e) {
      if (get().selectedId === id) set({ error: errText(e), detailLoading: false });
    }
  },

  setTab: async (tab) => {
    if (!DETAIL_TABS.includes(tab)) return;
    set({ detailTab: tab });
    const { selectedId, raw } = get();
    if (tab === 'raw' && raw == null && selectedId != null) {
      try {
        const text = await svc.raw(selectedId);
        if (get().selectedId === selectedId) set({ raw: text });
      } catch (e) {
        set({ error: errText(e) });
      }
    }
  },

  /**
   * The two destructive paths resolve ALWAYS (fix round 1, NB8): a failed
   * invoke lands in `error` (the `load`/`select` shape) instead of escaping
   * `ConfirmModal.onConfirm` as an unhandled rejection that shows nothing.
   */
  remove: async (ids) => {
    const list = Array.isArray(ids) ? ids : [ids];
    if (list.length === 0) return;
    try {
      await svc.remove(list);
    } catch (e) {
      set({ error: errText(e) });
      return;
    }
    set((s) => {
      const gone = new Set(list);
      const removedUnread = s.messages.filter((m) => gone.has(m.id) && !m.read).length;
      const removedListed = s.messages.filter((m) => gone.has(m.id)).length;
      const dropSelection = gone.has(s.selectedId);
      return {
        messages: s.messages.filter((m) => !gone.has(m.id)),
        total: Math.max(0, s.total - removedListed),
        unread: Math.max(0, s.unread - removedUnread),
        status: {
          ...s.status,
          count: Math.max(0, s.status.count - removedListed),
          unread: Math.max(0, s.status.unread - removedUnread),
        },
        selectedId: dropSelection ? null : s.selectedId,
        detail: dropSelection ? null : s.detail,
        raw: dropSelection ? null : s.raw,
      };
    });
  },

  clear: async () => {
    try {
      await svc.clear();
    } catch (e) {
      set({ error: errText(e) });
      return;
    }
    set((s) => ({
      messages: [],
      total: 0,
      unread: 0,
      selectedId: null,
      detail: null,
      detailLoading: false,
      raw: null,
      status: { ...s.status, count: 0, unread: 0 },
    }));
  },

  /** `mailcatcher:message` — prepend, bump counts, dedupe by id. */
  onIncoming: (summary) => {
    if (!summary || summary.id == null) return;
    set((s) => {
      if (s.messages.some((m) => m.id === summary.id)) return {};
      const listed = rowMatchesQuery(summary, s.query);
      return {
        messages: listed ? [summary, ...s.messages] : s.messages,
        total: listed ? s.total + 1 : s.total,
        unread: s.unread + 1,
        status: { ...s.status, count: s.status.count + 1, unread: s.status.unread + 1 },
      };
    });
  },

  /** `mailcatcher:status` — the accept loop died; never show a dead server as running. */
  onStatus: (status) => {
    if (!status) return;
    set((s) => ({ status: { ...s.status, ...status } }));
  },

  /**
   * `sync:db-switched` — Rust swapped the account db under the listener
   * (sign-in / sign-out / account switch). Captured mail lives in that file,
   * so everything mirrored from the previous one goes SYNCHRONOUSLY (the
   * previous account's rows must not stay on screen for even a frame), then
   * status + list are re-read from the new file. The listener did not stop
   * (`running`/`port`/`lastError` stay) and the filter is the user's
   * (`query` stays). Not `auth:logout`: that fires before the file changes.
   */
  onAccountSwitched: () => {
    dbGeneration += 1;
    set((s) => ({
      messages: [],
      total: 0,
      unread: 0,
      selectedId: null,
      detail: null,
      detailLoading: false,
      raw: null,
      error: null,
      status: { ...s.status, count: 0, unread: 0 },
    }));
    get().refreshStatus();
    return get().load();
  },
}));

/**
 * Boot hook (App.js): once preferences are hydrated, start the listener on
 * the preferred port when `mailCatcherAutoStart` is on. Off by default (D2):
 * a client grabbing 1025 on every launch collides with a user's own Mailpit.
 * Returns the effect cleanup. Errors land in `status.lastError` via `start`.
 */
export function autoStartOnBoot() {
  if (!isTauri()) return () => {};
  const run = (prefs) => {
    if (prefs.mailCatcherAutoStart !== true) return;
    useMailCatcherStore.getState().start(prefs.mailCatcherPort);
  };
  const initial = usePreferencesStore.getState();
  if (initial.hydrated) {
    run(initial);
    return () => {};
  }
  let done = false;
  const unsubscribe = usePreferencesStore.subscribe((prefs) => {
    if (done || !prefs.hydrated) return;
    done = true;
    unsubscribe();
    run(prefs);
  });
  return () => {
    done = true;
    unsubscribe();
  };
}

/** Rail badge selector: the unread count from STATUS (server truth, kept in
 *  step by refreshStatus / onIncoming / select) — the list may never have
 *  been loaded when the Mail page was not opened this session. */
export const selectMailUnread = (s) => s.status.unread || 0;

let badgeSubscription = null;

/**
 * Boot hook (App.js): the rail badge needs the unread count and live
 * arrivals WITHOUT the Mail page ever being opened, so the app subscribes
 * to the three Tauri events once per process and refreshes the status. The
 * page keeps its own subscription — `onIncoming` dedupes by id, so a
 * message seen twice bumps nothing twice.
 */
export function bootMailBadge() {
  if (!isTauri()) return () => {};
  useMailCatcherStore.getState().refreshStatus();
  if (!badgeSubscription) {
    badgeSubscription = svc.subscribe({
      onMessage: (summary) => useMailCatcherStore.getState().onIncoming(summary),
      onStatus: (next) => useMailCatcherStore.getState().onStatus(next),
      // The account db swapped: drop the previous account's rows, re-read.
      onDbSwitched: () => useMailCatcherStore.getState().onAccountSwitched(),
    });
  }
  // Process-lifetime subscription: nothing to clean up on unmount.
  return () => {};
}

/** Test seam: drop the process-wide badge subscription. */
export function __resetMailBadgeForTests() {
  badgeSubscription?.unlisten();
  badgeSubscription = null;
}

